<script setup lang="ts">
import {computed, inject, reactive, ref} from "vue";
import {Files} from "@/Components/Global";
import {Avatar, Button, Icon, Popover, Select, Skeleton, SpeedDial} from "@/Components/Library";
import languages from "@/Data/languages.js";
import User from "@/Models/User";
import type {Dayjs} from "dayjs";
import {
    deleteConversationMessage,
    restoreConversationMessage,
    translateConversationMessage,
} from "@/Services/conversation-messages";
import {useRoute} from "vue-router";
import {useToast} from "primevue/usetoast";
import markdownit from "markdown-it";

type ConversationMessageType = {
    id: string;
    body: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
};
type ConversationMessageLocal = ConversationMessageType & {
    sender: {
        id: string;
        name: string;
        color: string;
    };
};

const $date = inject("date") as (date?: Date | string) => Dayjs;
const $user = new User();
const currentRoute = useRoute();
const toast = useToast();
const md = markdownit();

const props = defineProps<{
    ind: number;
    // message: ConversationMessageLocal;
    message: any;
    isSameDate: boolean;
    isSameSender: boolean;
    isAdmin: boolean;
}>();

const emit = defineEmits<{
    deleted: any;
    restored: any;
    attachmentDeleted: any;
    show: any;
}>();

const state = reactive({
    loading: {
        translate: false,
    },
});

const language = ref<{
    name: string;
    code: string;
} | null>(null);
const messageBody = ref<HTMLElement | null>(null);
const bodyText = ref(props.message.body);
const container = ref<HTMLDivElement | null>(null);
const menuPanel = ref<Popover | null>(null);
const menuItems = ref([
    {
        label: "Copy",
        icon: "prime:copy",
        class: "bg-askr-green-500 p-button-success",
        style: "color: white",
        severity: "success",
        command: () => {
            console.log("Copy", messageBody.value?.textContent);
            !!props.message.body &&
                !!messageBody.value &&
                messageBody.value.textContent &&
                navigator.clipboard.writeText(messageBody.value.textContent);
            toast.add({
                severity: "success",
                summary: "Copied",
                detail: "Copied to clipboard",
                life: 3000,
            });
        },
    },
    // {
    //     label: "Translate",
    //     icon: "prime:language",
    //     command: (event: MenuItemCommandEvent) => {
    //         console.log(event);
    //         toggleMenu(event.originalEvent);
    //     },
    // },
]);

const hideMenuItem = {
    label: "Hide",
    icon: "prime:trash",
    command: async () => {
        try {
            await deleteConversationMessage(currentRoute.params.conversation, props.message.id);
            props.message.deleted_at = $date().toISOString();
            if (props.isAdmin) {
                menuItems.value.push(restoreMenuItem);
                menuItems.value = menuItems.value.filter((M) => M.label !== "Hide");
            }
            emit("deleted", props.message);
            toast.add({
                severity: "success",
                summary: "Success",
                detail: "Message deleted successfully",
                life: 3000,
            });
        } catch (error) {
            console.log(error);
        }
    },
};

const restoreMenuItem = {
    label: "Restore",
    icon: "prime:undo",
    command: async () => {
        try {
            await restoreConversationMessage(currentRoute.params.conversation, {message: props.message.id});
            if (props.isAdmin) {
                menuItems.value.push(hideMenuItem);
                menuItems.value = menuItems.value.filter((M) => M.label !== "Restore");
            }
            emit("restored", props.message);
            toast.add({
                severity: "success",
                summary: "Success",
                detail: "Message restored successfully",
                life: 3000,
            });
        } catch (error) {
            console.log(error);
        }
    },
};

const toggleMenu = (event: Event) => {
    if (!menuPanel.value) return;

    menuPanel.value.toggle(event);
};

if (
    (props.message.sender.id === $user.id && $date(props.message.created_at).isAfter($date().subtract(5, "minute"))) ||
    (props.isAdmin && !props.message.deleted_at)
) {
    menuItems.value.push(hideMenuItem);
}

if (props.isAdmin && props.message.deleted_at) {
    menuItems.value.push(restoreMenuItem);
}

const setMenuItems = (message: ConversationMessageLocal, toggleCallback: (event: Event) => void) => {
    if (
        (message.sender.id !== $user.id || $date(message.created_at).isBefore($date().subtract(5, "minute"))) &&
        !props.isAdmin
    ) {
        menuItems.value = menuItems.value.filter((M) => M.label !== "Hide");
    } else {
        if (!menuItems.value.find((M) => M.label === "Hide") && !props.message.deleted_at) {
            menuItems.value.push(hideMenuItem);
        }
    }
    toggleCallback(new Event("click"));
};

const translateMessage = async () => {
    try {
        state.loading.translate = true;
        menuPanel.value?.hide();
        const {data} = await translateConversationMessage(currentRoute.params.conversation, props.message.id, {
            language: language.value,
        });
        console.log(data);
        bodyText.value = data.result.text;
    } catch (error) {
        console.log(error);
    } finally {
        state.loading.translate = false;
    }
};

const messageDate = (message: ConversationMessageType) => {
    return $date(message.created_at).format("ddd Do MMM YYYY [GMT]");
};

const borderTopColor = computed(() => {
    return props.message.sender.profile.color.class ?? (props.message.sender.id === $user.id ? "#496773" : "#d15b3e");
});

defineExpose({
    message: props.message,
    container,
});
</script>
<template>
    <div ref="container">
        <div
            v-if="!isSameDate"
            class="flex items-center justify-center pb-8 text-stone-500 dark:text-stone-400"
            :class="[ind > 0 ? 'pt-8' : 'pt-3']"
        >
            <hr class="grow border-stone-300 dark:border-stone-800" />
            <span class="upper-case mx-6 text-sm font-semibold">
                <small>{{ messageDate(message) }}</small>
            </span>
            <hr class="grow border-stone-300 dark:border-stone-800" />
        </div>
        <div class="flex items-start" :class="[!isSameSender && isSameDate ? 'mt-6' : 'mt-0.5']">
            <button class="mr-3 w-8" @click="emit('show', message.sender)">
                <Avatar
                    v-if="!isSameSender && message.sender.id !== $user.id"
                    :user="message.sender"
                    class="flex h-8 w-8 shrink-0 text-xs tracking-wider"
                    shape="circle"
                />
            </button>
            <div
                class="relative w-full max-w-xl rounded-md border bg-white px-3 py-3 dark:border-stone-800 dark:bg-stone-900"
                :class="[
                    {
                        'shadow-sm': !message.deleted_at,
                        'opacity-50': message.deleted_at,
                        'ml-auto': message.sender.id === $user.id,
                        'border-t-4': !isSameDate || !isSameSender,
                    },
                    !isSameDate || !isSameSender ? `!border-t-${borderTopColor} dark:!border-t-${borderTopColor}` : '',
                ]"
            >
                <SpeedDial
                    :model="menuItems"
                    direction="left"
                    class="absolute right-0 top-0.5 z--10"
                    :tooltipOptions="{position: 'top', event: undefined}"
                >
                    <template #button="{toggleCallback}">
                        <button class="px-1.5" @click="setMenuItems(message, toggleCallback)">
                            <Icon icon="mdi:dots-vertical" height="16" />
                        </button>
                    </template>
                    <template #item="{item, toggleCallback}">
                        <Button
                            :icon="{icon: item.icon}"
                            :class="item.class"
                            class="bg-stone-600 !p-1"
                            @click="toggleCallback"
                        />
                    </template>
                </SpeedDial>
                <div
                    class="mb-1 mr-6 text-xs font-semibold dark:brightness-150"
                    :class="[`text-${message.sender.profile.color.class}`]"
                    v-if="!isSameSender || !isSameDate"
                >
                    <button @click="emit('show', message.sender)">
                        {{ message.sender?.name }}
                    </button>
                </div>
                <div v-if="state.loading.translate">
                    <Skeleton class="mb-2"></Skeleton>
                    <Skeleton class="mb-2"></Skeleton>
                </div>
                <div
                    class="prose prose-stone mb-2 break-words dark:prose-invert"
                    v-else-if="props.message.body"
                    v-html="md.render(props.message.body)"
                    ref="messageBody"
                ></div>
                <Files
                    v-if="message.attachments?.length"
                    modelType="conversation-message"
                    :modelId="message.id"
                    collection="attachment"
                    :files="message.attachments"
                    class="mt-8"
                    @delete="emit('attachmentDeleted', message, $event)"
                />
                <div class="flex justify-end text-xs font-semibold dark:text-stone-500">
                    {{ $date(message.created_at).format("HH:mm") }}
                </div>
            </div>
        </div>
        <Popover ref="menuPanel" class="w-80">
            <Select
                v-model="language"
                :options="languages"
                filter
                optionLabel="name"
                optionValue="code"
                @update:modelValue="translateMessage"
                placeholder="Select a Language"
                class="w-full"
            >
                <!--                <template #value="slotProps">-->
                <!--                    <div v-if="slotProps.value" class="flex align-items-center">-->
                <!--                        <img :alt="slotProps.value.label"-->
                <!--                             src="https://primefaces.org/cdn/primevue/images/flag/flag_placeholder.png"-->
                <!--                             :class="`mr-2 flag flag-${slotProps.value.code.toLowerCase()}`" style="width: 18px"/>-->
                <!--                        <div>{{ slotProps.value.name }}</div>-->
                <!--                    </div>-->
                <!--                    <span v-else>-->
                <!--                        {{ slotProps.placeholder }}-->
                <!--                    </span>-->
                <!--                </template>-->
                <!--                <template #option="slotProps">-->
                <!--                    <div class="flex align-items-center">-->
                <!--                        <img :alt="slotProps.option.label"-->
                <!--                             src="https://primefaces.org/cdn/primevue/images/flag/flag_placeholder.png"-->
                <!--                             :class="`mr-2 flag flag-${slotProps.option.code.toLowerCase()}`" style="width: 18px"/>-->
                <!--                        <div>{{ slotProps.option.name }}</div>-->
                <!--                    </div>-->
                <!--                </template>-->
            </Select>
        </Popover>
    </div>
</template>
<style scoped></style>
